/**
 * Frontend authentication helpers.
 */

import { API_CONFIG } from './config'

export interface User {
  id: number
  username: string
  phone?: string
  is_active: boolean
  is_admin: boolean
  real_name_verified?: boolean
  real_name?: string | null
  id_card_masked?: string | null
  verified_at?: string | null
  verify_provider?: string | null
  created_at: string
}

export interface LoginResponse {
  access_token: string
  token_type: string
}

export interface RealNameVerifyPayload {
  real_name: string
  id_card_no: string
  agreed_to_real_name_auth: boolean
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function isLoginResponse(value: unknown): value is LoginResponse {
  return isRecord(value)
    && typeof value.access_token === 'string'
    && typeof value.token_type === 'string'
}

function parseJsonText(rawText: string): unknown | null {
  if (!rawText.trim()) {
    return null
  }

  try {
    return JSON.parse(rawText)
  } catch {
    return null
  }
}

async function readResponsePayload(response: Response): Promise<{
  rawText: string
  data: unknown
}> {
  const rawText = await response.text()
  return {
    rawText,
    data: parseJsonText(rawText),
  }
}

function pickMessage(data: unknown, rawText: string, fallback: string): string {
  if (isRecord(data)) {
    const detail = data.detail
    const message = data.message

    if (typeof detail === 'string' && detail.trim()) {
      return detail
    }

    if (typeof message === 'string' && message.trim()) {
      return message
    }
  }

  return rawText.trim() || fallback
}

export function getToken(): string | null {
  return localStorage.getItem('access_token')
}

export function saveToken(token: string): void {
  localStorage.setItem('access_token', token)
}

export function clearToken(): void {
  localStorage.removeItem('access_token')
}

export async function sendVerificationCode(phone: string): Promise<string> {
  try {
    const response = await fetch(`${API_CONFIG.baseURL}/auth/send-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone })
    })

    const { rawText, data } = await readResponsePayload(response)

    if (response.ok) {
      return pickMessage(data, rawText, '验证码已发送')
    }

    throw new Error(pickMessage(data, rawText, '发送失败，请稍后重试'))
  } catch (error) {
    console.error('发送验证码错误:', error)
    throw error
  }
}

export async function loginWithCode(phone: string, code: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_CONFIG.baseURL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone, code })
    })

    const { rawText, data } = await readResponsePayload(response)

    if (response.ok) {
      if (!isLoginResponse(data)) {
        throw new Error('登录响应格式不正确')
      }

      saveToken(data.access_token)
      return true
    }

    throw new Error(pickMessage(data, rawText, '登录失败'))
  } catch (error) {
    console.error('登录错误:', error)
    throw error
  }
}

export async function login(username: string | null, password: string, phone?: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_CONFIG.baseURL}/auth/login-json`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password, phone: phone || null })
    })

    const { rawText, data } = await readResponsePayload(response)

    if (response.ok) {
      if (!isLoginResponse(data)) {
        throw new Error('登录响应格式不正确')
      }

      saveToken(data.access_token)
      return true
    }

    throw new Error(pickMessage(data, rawText, '登录失败'))
  } catch (error) {
    console.error('登录错误:', error)
    throw error
  }
}

export function logout(): void {
  clearToken()
  window.location.reload()
}

export async function getCurrentUser(): Promise<User | null> {
  const token = getToken()
  if (!token) {
    return null
  }

  try {
    const response = await fetch(`${API_CONFIG.baseURL}/auth/me`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    const { data } = await readResponsePayload(response)

    if (response.ok && isRecord(data)) {
      return data as unknown as User
    }

    clearToken()
    return null
  } catch (error) {
    console.error('获取当前用户信息错误:', error)
    return null
  }
}

export async function verifyRealName(payload: RealNameVerifyPayload): Promise<User> {
  try {
    const response = await fetch(`${API_CONFIG.baseURL}/auth/real-name/verify`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload)
    })

    const { rawText, data } = await readResponsePayload(response)

    if (response.ok && isRecord(data)) {
      return data as unknown as User
    }

    throw new Error(pickMessage(data, rawText, '实名认证失败，请稍后重试'))
  } catch (error) {
    console.error('实名认证错误:', error)
    throw error
  }
}

export async function checkAuth(): Promise<boolean> {
  const user = await getCurrentUser()
  return user !== null
}

export function getAuthHeaders(): HeadersInit {
  const token = getToken()
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  }
}

export async function register(username: string, password: string, phone?: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_CONFIG.baseURL}/auth/register-public`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password, phone: phone || null })
    })

    const { rawText, data } = await readResponsePayload(response)

    if (response.ok) {
      return true
    }

    throw new Error(pickMessage(data, rawText, '注册失败'))
  } catch (error) {
    console.error('注册错误:', error)
    throw error
  }
}

export async function getAllUsers(): Promise<User[]> {
  try {
    const response = await fetch(`${API_CONFIG.baseURL}/auth/users`, {
      headers: getAuthHeaders()
    })

    const { rawText, data } = await readResponsePayload(response)

    if (response.ok && Array.isArray(data)) {
      return data as User[]
    }

    throw new Error(pickMessage(data, rawText, '获取用户列表失败'))
  } catch (error) {
    console.error('获取用户列表错误:', error)
    throw error
  }
}

export async function toggleUserActive(userId: number): Promise<User> {
  try {
    const response = await fetch(`${API_CONFIG.baseURL}/auth/users/${userId}/toggle-active`, {
      method: 'PATCH',
      headers: getAuthHeaders()
    })

    const { rawText, data } = await readResponsePayload(response)

    if (response.ok && isRecord(data)) {
      return data as unknown as User
    }

    throw new Error(pickMessage(data, rawText, '切换用户状态失败'))
  } catch (error) {
    console.error('切换用户状态错误:', error)
    throw error
  }
}

export async function deleteUser(userId: number): Promise<void> {
  try {
    const response = await fetch(`${API_CONFIG.baseURL}/auth/users/${userId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    })

    if (response.ok) {
      return
    }

    const { rawText, data } = await readResponsePayload(response)
    throw new Error(pickMessage(data, rawText, '删除用户失败'))
  } catch (error) {
    console.error('删除用户错误:', error)
    throw error
  }
}
